import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { IPL_TEAMS } from '../utils/teams';

const logoPath = (shortName) => process.env.PUBLIC_URL + `/assets/assets/${shortName?.toLowerCase()}.png`;

function TeamLogo({ shortName, size = 48, style = {} }) {
  return (
    <img
      src={logoPath(shortName)}
      alt={shortName}
      width={size}
      height={size}
      style={{ objectFit: 'contain', display: 'block', maxWidth: '100%', height: 'auto', ...style }}
      onError={e => {
        e.target.style.display = 'none';
        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
      }}
    />
  );
}

function LogoFallback({ shortName, size, color }) {
  return (
    <div style={{
      display: 'none', width: size, height: size, borderRadius: '50%',
      background: `${color}22`, border: `2px solid ${color}55`,
      alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Bebas Neue, sans-serif', fontSize: size * 0.35,
      color: color, letterSpacing: '0.04em',
    }}>{shortName?.slice(0, 2)}</div>
  );
}

export default function MatchCard({ match }) {
  const { team1, team2, venue, date, time, matchType, ticketCategories } = match;
  const minPrice      = ticketCategories ? Math.min(...ticketCategories.map(c => c.price)) : 0;
  const totalAvail    = ticketCategories ? ticketCategories.reduce((s, c) => s + c.availableSeats, 0) : 0;
  const isFinal       = matchType === 'final';
  const isPlayoff     = ['qualifier', 'eliminator', 'final'].includes(matchType);
  const t1            = IPL_TEAMS[team1.shortName] || {};
  const t2            = IPL_TEAMS[team2.shortName] || {};
  const c1            = team1.color || '#00E5FF';
  const c2            = team2.color || '#0055FF';
  const isAlmostFull  = totalAvail > 0 && totalAvail < 5000;
  const isSoldOut     = totalAvail === 0;

  return (
    <div
      className="match-card-wrapper"
      style={{
        position: 'relative', background: '#0A0D1A', borderRadius: 16,
        overflow: 'hidden', border: '1px solid rgba(0, 229, 255, 0.1)',
        transition: 'transform 0.22s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.22s',
        fontFamily: 'Outfit, sans-serif',
        display: 'flex', flexDirection: 'column', height: '100%'
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-6px)';
        e.currentTarget.style.boxShadow = `0 20px 40px rgba(0,0,0,0.8), 0 0 0 1px rgba(0, 229, 255, 0.3)`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* top gradient bar */}
      <div style={{ height: 4, background: `linear-gradient(90deg, ${c1} 0%, #0A0D1A 45%, ${c2} 100%)` }} />

      {/* playoff badge */}
      {isPlayoff && (
        <div style={{
          position: 'absolute', top: 12, right: 12, zIndex: 3,
          background: isFinal ? 'linear-gradient(135deg, #0055FF, #00E5FF)' : 'rgba(0, 229, 255, 0.1)',
          color: '#fff',
          border: isFinal ? 'none' : '1px solid rgba(0, 229, 255, 0.3)',
          padding: '4px 12px', borderRadius: 20,
          fontFamily: 'Bebas Neue, sans-serif', fontSize: 12, letterSpacing: '0.12em',
        }}>
          {isFinal ? '★ FINAL' : matchType.toUpperCase()}
        </div>
      )}

      <div style={{ padding: '20px', position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {/* meta row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 10 }}>
          <div style={{ flexShrink: 1, minWidth: 0 }}>
            <span style={{
              display: 'inline-block', marginBottom: 8,
              background: 'rgba(0, 229, 255, 0.1)', color: '#00E5FF',
              border: '1px solid rgba(0, 229, 255, 0.2)',
              padding: '3px 10px', borderRadius: 20,
              fontFamily: 'Rajdhani, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
              whiteSpace: 'nowrap'
            }}>MATCH {match.matchNumber}</span>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              📍 {venue.name}, {venue.city}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>
              {date ? format(new Date(date), 'dd MMM yyyy') : '—'}
            </div>
            <div style={{ fontSize: 12, color: '#00E5FF', fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, marginTop: 2 }}>{time}</div>
          </div>
        </div>

        {/* teams */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          {/* team1 */}
          <Link to={`/team/${team1.shortName}`} style={{ textDecoration: 'none', flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
              <div style={{
                width: 'clamp(50px, 15vw, 68px)', height: 'clamp(50px, 15vw, 68px)', borderRadius: '50%',
                background: `rgba(255,255,255,0.03)`, border: `2px solid ${c1}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 15px ${c1}40`,
              }}>
                <TeamLogo shortName={team1.shortName} size={42} />
              </div>
              <div style={{ width: '100%' }}>
                <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 'clamp(22px, 6vw, 28px)', lineHeight: 1, color: c1, letterSpacing: '0.03em' }}>{team1.shortName}</div>
              </div>
            </div>
          </Link>

          {/* VS */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 8px', flexShrink: 0 }}>
            <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 'clamp(28px, 8vw, 36px)', lineHeight: 1, color: '#fff', letterSpacing: '0.02em' }}>VS</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, letterSpacing: '0.2em', marginTop: 2 }}>T20</div>
          </div>

          {/* team2 */}
          <Link to={`/team/${team2.shortName}`} style={{ textDecoration: 'none', flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              <div style={{
                width: 'clamp(50px, 15vw, 68px)', height: 'clamp(50px, 15vw, 68px)', borderRadius: '50%',
                background: `rgba(255,255,255,0.03)`, border: `2px solid ${c2}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 15px ${c2}40`,
              }}>
                <TeamLogo shortName={team2.shortName} size={42} />
              </div>
              <div style={{ width: '100%', textAlign: 'right' }}>
                <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 'clamp(22px, 6vw, 28px)', lineHeight: 1, color: c2, letterSpacing: '0.03em' }}>{team2.shortName}</div>
              </div>
            </div>
          </Link>
        </div>

        <div style={{ flexGrow: 1 }} />
        <div style={{ height: 1, margin: '16px 0', background: 'rgba(255,255,255,0.05)' }} />

        {/* price + CTA */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: 'Rajdhani, sans-serif', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 4 }}>Tickets From</div>
            <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 26, color: '#00E5FF', lineHeight: 1, letterSpacing: '0.02em' }}>
              ₹{minPrice.toLocaleString('en-IN')}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, letterSpacing: '0.05em', color: isSoldOut ? '#ff4444' : isAlmostFull ? '#00E5FF' : 'rgba(255,255,255,0.5)' }}>
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: isSoldOut ? '#ff4444' : isAlmostFull ? '#00E5FF' : '#22c55e' }} />
              {isSoldOut ? 'SOLD OUT' : isAlmostFull ? 'FILLING FAST' : `${totalAvail.toLocaleString()} seats`}
            </div>
            <Link
              to={`/matches/${match._id}`}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '10px 24px', borderRadius: 8, textDecoration: 'none',
                background: isSoldOut ? 'rgba(255,255,255,0.05)' : 'linear-gradient(90deg, #0055FF, #00E5FF)',
                color: isSoldOut ? 'rgba(255,255,255,0.3)' : '#fff',
                fontFamily: 'Bebas Neue, sans-serif', fontSize: 18, letterSpacing: '0.05em',
                boxShadow: isSoldOut ? 'none' : '0 6px 20px rgba(0, 85, 255, 0.3)',
                pointerEvents: isSoldOut ? 'none' : 'auto',
                transition: 'all 0.2s',
              }}
            >
              🎟 {isSoldOut ? 'Unavailable' : 'Book Now'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}